import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtIDR, fmtDate } from "@/lib/format";
import { ChevronLeft, ChevronRight, CalendarDays, Stethoscope, Receipt, FileText, Clock, Wallet } from "lucide-react";

const txStatusBadge = (s) => {
  const map = {
    completed: "bg-emerald-100 text-emerald-800",
    refunded: "bg-rose-100 text-rose-700",
  };
  const label = { completed: "Lunas", refunded: "Refund" }[s] || s;
  return <Badge className={`${map[s] || "bg-slate-100 text-slate-700"} border-0 text-[10px]`}>{label}</Badge>;
};

export function VisitHistoryDialog({ open, onOpenChange, patient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [depHistory, setDepHistory] = useState([]);

  useEffect(() => {
    if (open && patient?.id) {
      setSelected(null);
      setData(null);
      setDeposits([]);
      setDepHistory([]);
      setLoading(true);
      api.get(`/patients/${patient.id}/treatment-deposits`).then((r) => setDeposits(r.data)).catch(() => {});
      api.get(`/patients/${patient.id}/treatment-deposit-history`).then((r) => setDepHistory(r.data)).catch(() => {});
      api
        .get(`/patients/${patient.id}/visit-history`)
        .then((r) => setData(r.data))
        .finally(() => setLoading(false));
    }
  }, [open, patient]);

  const visits = data?.visits || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="visit-history-dialog" className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-emerald-600" />
            Riwayat Kunjungan
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {patient?.name}
            {patient?.phone ? ` · ${patient.phone}` : ""}
            {data?.patient?.medical_record_no ? ` · RM ${data.patient.medical_record_no}` : ""}
          </p>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-4" style={{ WebkitOverflowScrolling: "touch" }}>
          {!selected && (
            <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-3 text-sm" data-testid="profile-treatment-deposits">
              <div className="flex items-center gap-2 font-medium text-sky-800"><Wallet className="h-4 w-4" /> Deposit Treatment</div>

              {/* Sisa saldo */}
              <div className="mt-2">
                <div className="text-[11px] font-medium uppercase tracking-wide text-sky-700/80">Sisa Saldo</div>
                {deposits.length > 0 ? (
                  <div className="mt-1 space-y-0.5">
                    {deposits.map((d) => (
                      <div key={d.treatment_id} className="flex justify-between text-sky-900">
                        <span>{d.treatment_name}</span>
                        <b>
                          {Number(d.sessions_remaining) > 0 ? `${d.sessions_remaining} sesi` : ""}
                          {Number(d.sessions_remaining) > 0 && Number(d.nominal_remaining) > 0 ? " + " : ""}
                          {Number(d.nominal_remaining) > 0 ? fmtIDR(d.nominal_remaining) : ""}
                        </b>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-muted-foreground" data-testid="profile-deposit-balance-empty">Tidak ada saldo deposit treatment aktif.</div>
                )}
              </div>

              {/* Riwayat top-up & pemakaian */}
              <div className="mt-3" data-testid="profile-deposit-history">
                <div className="text-[11px] font-medium uppercase tracking-wide text-sky-700/80">Riwayat</div>
                {depHistory.length > 0 ? (
                  <div className="mt-1 space-y-1.5">
                    {depHistory.map((h, i) => (
                      <div key={i} className="flex items-center justify-between gap-2" data-testid={`dep-history-${i}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 text-xs">
                            <Badge className={`${h.type === "topup" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} border-0 text-[10px]`}>
                              {h.type === "topup" ? "Top Up" : "Pemakaian"}
                            </Badge>
                            <span className="truncate font-medium">{h.treatment_name || "Treatment"}</span>
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {fmtDate(h.date)}
                            {h.type === "use" && (h.mode === "session" ? ` · ${h.sessions || 1} sesi` : " · potong saldo")}
                            {h.status === "refunded" ? " · (refund)" : ""}
                          </div>
                        </div>
                        <span className={`shrink-0 text-xs font-medium tabular-nums ${h.type === "topup" ? "text-emerald-700" : "text-amber-700"}`}>
                          {h.type === "topup" ? "+" : "−"}{fmtIDR(h.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1 text-xs text-muted-foreground" data-testid="profile-deposit-history-empty">Belum ada transaksi deposit treatment untuk pasien ini.</div>
                )}
              </div>
            </div>
          )}
          {loading && <div className="py-10 text-center text-sm text-muted-foreground">Memuat riwayat...</div>}

          {!loading && !selected && visits.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="visit-history-empty">
              Belum ada riwayat kunjungan untuk pasien ini.
            </div>
          )}

          {/* LIST VIEW */}
          {!loading && !selected && visits.length > 0 && (
            <div className="space-y-2" data-testid="visit-history-list">
              {visits.map((v) => (
                <button
                  key={v.date}
                  type="button"
                  data-testid={`visit-row-${v.date}`}
                  onClick={() => setSelected(v)}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/40"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <CalendarDays className="h-4 w-4 text-emerald-600 shrink-0" />
                      {fmtDate(v.date)}
                    </div>
                    <div className="mt-1 truncate text-xs text-muted-foreground">
                      {v.treatments.length > 0 ? v.treatments.join(", ") : "Tanpa treatment tercatat"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {v.total_paid > 0 && (
                      <span className="text-xs font-medium text-emerald-700">{fmtIDR(v.total_paid)}</span>
                    )}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* DETAIL VIEW */}
          {!loading && selected && (
            <div className="space-y-4" data-testid="visit-detail">
              <Button variant="ghost" size="sm" className="-ml-2 h-8" data-testid="visit-back-btn" onClick={() => setSelected(null)}>
                <ChevronLeft className="mr-1 h-4 w-4" /> Kembali ke daftar
              </Button>

              <div className="rounded-lg bg-secondary/40 px-4 py-3">
                <div className="flex items-center gap-2 font-medium">
                  <CalendarDays className="h-4 w-4 text-emerald-600" /> {fmtDate(selected.date)}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Treatment: {selected.treatments.length > 0 ? selected.treatments.join(", ") : "—"}
                </div>
              </div>

              {/* Medical records */}
              <section>
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
                  <Stethoscope className="h-4 w-4 text-indigo-600" /> Rekam Medis Dokter/Therapist
                </h3>
                {selected.medical_records.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Tidak ada rekam medis pada kunjungan ini.</p>
                ) : (
                  <div className="space-y-2">
                    {selected.medical_records.map((m) => (
                      <div key={m.id} className="rounded-lg border border-border p-3 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">{m.treatment_name || "Treatment"}</span>
                          <span className="text-xs text-muted-foreground">{m.doctor_name || "—"}</span>
                        </div>
                        {m.diagnosis && (
                          <div className="mt-1 text-xs">
                            <span className="text-muted-foreground">Diagnosis: </span>
                            {m.diagnosis}{m.icd10_code ? ` (${m.icd10_code})` : ""}
                          </div>
                        )}
                        {m.notes && (
                          <div className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                            <FileText className="mr-1 inline h-3 w-3" />{m.notes}
                          </div>
                        )}
                        {m.next_visit_at && (
                          <div className="mt-1 text-xs text-muted-foreground">Kontrol berikutnya: {fmtDate(m.next_visit_at)}</div>
                        )}
                        {(m.before_photo_url || m.after_photo_url) && (
                          <div className="mt-2 flex gap-2">
                            {m.before_photo_url && <img src={m.before_photo_url} alt="before" className="h-16 w-16 rounded object-cover" />}
                            {m.after_photo_url && <img src={m.after_photo_url} alt="after" className="h-16 w-16 rounded object-cover" />}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Transactions */}
              <section>
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
                  <Receipt className="h-4 w-4 text-amber-600" /> Transaksi Pembayaran
                </h3>
                {selected.transactions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Tidak ada transaksi pada kunjungan ini.</p>
                ) : (
                  <div className="space-y-2">
                    {selected.transactions.map((t) => (
                      <div key={t.id} className="rounded-lg border border-border p-3" data-testid={`visit-tx-${t.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {txStatusBadge(t.status)}
                            <span className="text-xs capitalize text-muted-foreground">{t.payment_method || "—"}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</span>
                        </div>
                        <div className="mt-2 divide-y divide-border/60">
                          {t.items.map((it, i) => (
                            <div key={i} className="flex items-center justify-between py-1 text-xs">
                              <span className="min-w-0 truncate">
                                {it.name}
                                <span className="text-muted-foreground"> × {it.quantity}</span>
                                {it.type === "product" && <span className="ml-1 text-[10px] text-sky-600">produk</span>}
                              </span>
                              <span className="shrink-0 tabular-nums">{fmtIDR(it.line_total)}</span>
                            </div>
                          ))}
                          {t.items.length === 0 && <div className="py-1 text-xs text-muted-foreground">Tidak ada rincian item.</div>}
                        </div>
                        <div className="mt-2 space-y-0.5 border-t border-border pt-2 text-xs">
                          {t.subtotal != null && (
                            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="tabular-nums">{fmtIDR(t.subtotal)}</span></div>
                          )}
                          {t.discount_amount ? (
                            <div className="flex justify-between text-rose-600"><span>Diskon</span><span className="tabular-nums">- {fmtIDR(t.discount_amount)}</span></div>
                          ) : null}
                          <div className="flex justify-between font-semibold"><span>Total</span><span className="tabular-nums">{fmtIDR(t.amount)}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <div className="rounded-lg bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-800">
                Total dibayar pada kunjungan ini: {fmtIDR(selected.total_paid)}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default VisitHistoryDialog;
